import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  FormControl,
  ValidationErrors,
  AsyncValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of, Observable } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  catchError,
  first,
} from 'rxjs/operators';
import {
  CustomFieldDefinition,
  CustomFieldType,
  VertextTypes,
  ValidationRules,
} from '../../../models/custom-field.model';
import { CustomFieldService } from '../../../services/custom-field.service';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/@shared';

@Component({
  selector: 'app-create-custom-field',
  templateUrl: './create-custom-field.component.html',
  styleUrls: ['./create-custom-field.component.scss'],
})
export class CreateCustomFieldComponent implements OnInit, OnDestroy {
  FieldTypes = CustomFieldType; // Expose enum to template
  VertexTypes = VertextTypes; // Expose enum to template
  private destroy$ = new Subject<void>();

  formGroup!: FormGroup;
  customFieldId: string = '0';

  // Track which validation rule fields should be visible
  showTextValidationRules: boolean = false;
  showNumberValidationRules: boolean = false;
  showPatternValidationRules: boolean = false;
  showDateValidationRules: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customFieldService: CustomFieldService,
    private fb: FormBuilder,
    private matSnackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    // Initialize form group with FormBuilder
    this.formGroup = this.fb.group({
      FieldName: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(64),
          this.fieldNameFormatValidator(),
        ],
        [this.fieldNameUniquenessValidator()],
      ],
      DisplayLabel: ['', [Validators.required, Validators.maxLength(255)]],
      VertexType: ['', [Validators.required]],
      FieldType: ['', [Validators.required]],
      ValidationRules: this.fb.group({
        required: [false],
        minLength: [null],
        maxLength: [null],
        min: [null],
        max: [null],
        pattern: [''],
        minDate: [''],
        maxDate: [''],
      }),
      DisplayOrder: [10, [Validators.required, Validators.min(10)]],
      IsReadOnly: [false, [Validators.required]],
      IsHidden: [false, [Validators.required]],
      Options: this.fb.array([]),
    });

    // Watch for FieldType changes to update validation rules visibility
    this.formGroup
      .get('FieldType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((fieldType: string) => {
        this.updateValidationRulesVisibility(fieldType);
        // Clear validation rules when field type changes
        this.resetValidationRules();
      });

    // Watch for VertexType changes to trigger field name uniqueness validation
    this.formGroup
      .get('VertexType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Re-validate field name when vertex type changes
        const fieldNameControl = this.formGroup.get('FieldName');
        if (fieldNameControl && fieldNameControl.value) {
          fieldNameControl.updateValueAndValidity();
        }
      });
  }

  ngOnInit(): void {
    // get the custom field id from the parent route
    this.route.params.subscribe((params) => {
      this.customFieldId = params['customFieldId'];
    });

    if (this.customFieldId !== '0') {
      this.customFieldService
        .getCustomFieldDefinition(this.customFieldId)
        .subscribe({
          next: (field: CustomFieldDefinition) => {
            // Parse validation rules from JSON string
            let validationRules: ValidationRules = {};
            if (field.ValidationRules) {
              try {
                validationRules = JSON.parse(field.ValidationRules);
              } catch (e) {
                console.error('Error parsing validation rules:', e);
              }
            }

            this.formGroup.patchValue({
              FieldName: field.FieldName,
              DisplayLabel: field.DisplayLabel,
              VertexType: field.VertexType,
              FieldType: field.FieldType,
              ValidationRules: validationRules,
              DisplayOrder: field.DisplayOrder,
              IsReadOnly: field.IsReadOnly,
              IsHidden: field.IsHidden,
            });

            this.formGroup.get('FieldName')?.disable();
            this.formGroup.get('FieldType')?.disable();
            // Populate options FormArray
            const optionsArray = this.formGroup.get('Options') as FormArray;
            optionsArray.clear();
            if (field.Options) {
              const options = field.Options.split(',')
                .map((opt) => opt.trim())
                .filter((opt) => opt);
              options.forEach((option) => {
                optionsArray.push(
                  this.fb.control(option, [Validators.required]),
                );
              });
            }

            // Update validation rules visibility based on field type
            this.updateValidationRulesVisibility(field.FieldType);
            this.formGroup.get('ValidationRules')?.patchValue(validationRules);
          },
        });
    } else {
      // Initialize with default field type if creating new
      this.updateValidationRulesVisibility(
        this.formGroup.get('FieldType')?.value || '',
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Validates field name format:
   * - Must be alphanumeric with underscores only
   * - Must start with a letter (not number or underscore)
   * - Must be 1-64 characters in length
   */
  fieldNameFormatValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const value = control.value as string;

      // Check length (1-64 characters)
      if (value.length < 1 || value.length > 64) {
        return { fieldNameLength: { message: 'Field name must be between 1 and 64 characters' } };
      }

      // Check if starts with a letter
      if (!/^[a-zA-Z]/.test(value)) {
        return { fieldNameStart: { message: 'Field name must start with a letter' } };
      }

      // Check if contains only alphanumeric characters and underscores
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(value)) {
        return { fieldNameFormat: { message: 'Field name must be alphanumeric with underscores only (no spaces or special characters)' } };
      }

      return null;
    };
  }

  /**
   * Validates field name uniqueness per vertex type
   * Uses async validator to check if field name already exists
   */
  fieldNameUniquenessValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || !this.formGroup) {
        return of(null);
      }

      const fieldName = control.value as string;
      const vertexType = this.formGroup.get('VertexType')?.value;

      // If no vertex type selected, skip uniqueness check
      if (!vertexType) {
        return of(null);
      }

      // If editing existing field, skip uniqueness check (it's already unique)
      if (this.customFieldId !== '0') {
        return of(null);
      }

      // Normalize field name to camelCase for comparison
      const normalizedFieldName = this.normalizeToCamelCase(fieldName);

      // If normalized name is empty, skip check
      if (!normalizedFieldName) {
        return of(null);
      }

      // Debounce the API call
      return of(control.value).pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() => {
          return this.customFieldService.getCustomFieldDefinitionsByVertexType(vertexType).pipe(
            map((fields: CustomFieldDefinition[]) => {
              // Check if any existing field has the same normalized field name
              const existingField = fields.find(
                (field) => this.normalizeToCamelCase(field.FieldName) === normalizedFieldName
              );

              if (existingField) {
                return { fieldNameUnique: { message: 'Field name must be unique per vertex type' } };
              }

              return null;
            }),
            catchError(() => of(null)) // On error, don't block form submission
          );
        }),
        first() // Complete the observable after first emission
      );
    };
  }

  /**
   * Normalizes field name to camelCase (e.g., "Campaign_ID" → "campaignId", "field_name" → "fieldName")
   * This matches the server-side normalization logic
   */
  normalizeToCamelCase(fieldName: string): string {
    if (!fieldName) {
      return '';
    }

    // Remove leading/trailing whitespace
    const trimmed = fieldName.trim();

    // Split by underscore, hyphen, or space
    const parts = trimmed.split(/[_\s-]+/).filter((p) => p.length > 0);

    if (parts.length === 0) {
      return '';
    }

    // First part lowercase
    let normalized = parts[0].toLowerCase();

    // Subsequent parts: first char uppercase, rest lowercase
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (part.length > 0) {
        normalized += part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
    }

    return normalized;
  }

  /**
   * Updates which validation rule fields are visible based on field type
   */
  updateValidationRulesVisibility(fieldType: string): void {
    // Reset all visibility flags
    this.showTextValidationRules = false;
    this.showNumberValidationRules = false;
    this.showPatternValidationRules = false;
    this.showDateValidationRules = false;

    // Set visibility based on field type
    switch (fieldType) {
      case this.FieldTypes.Text:
      case this.FieldTypes.Textarea:
        this.showTextValidationRules = true;
        this.showPatternValidationRules = true;
        break;
      case this.FieldTypes.Number:
        this.showNumberValidationRules = true;
        break;
      case this.FieldTypes.Email:
      case this.FieldTypes.Url:
      case this.FieldTypes.Phone:
        this.showPatternValidationRules = true;
        break;
      case this.FieldTypes.Date:
      case this.FieldTypes.DateTime:
        this.showDateValidationRules = true;
        break;
      default:
        // For other types (select, multiselect, radio, checkbox, toggle, color)
        // Only show required validation
        break;
    }

    // Update Options field validation based on field type
    const optionsArray = this.formGroup.get('Options') as FormArray;
    const requiresOptions = this.requiresOptions(fieldType);

    if (requiresOptions && optionsArray.length === 0) {
      optionsArray.push(this.fb.control('', [Validators.required]));
    }
  }

  /**
   * Checks if the field type requires options
   * Public method accessible from template
   */
  requiresOptions(fieldType: string): boolean {
    if (!fieldType) {
      return false;
    }
    const typesRequiringOptions = [
      this.FieldTypes.Select,
      this.FieldTypes.MultiSelect,
      this.FieldTypes.Radio,
    ];
    return typesRequiringOptions.includes(
      fieldType as CustomFieldType,
    );
  }

  getOptionsArray(): FormArray {
    return this.formGroup.get('Options') as FormArray;
  }

  getOptionControl(index: number): FormControl {
    return this.getOptionsArray().at(index) as FormControl;
  }

  addOption(): void {
    this.getOptionsArray().push(this.fb.control('', [Validators.required]));
  }

  removeOption(index: number): void {
    this.getOptionsArray().removeAt(index);
  }

  /**
   * Resets validation rules to default values
   */
  resetValidationRules(): void {
    this.formGroup.patchValue({
      ValidationRules: {
        required: false,
        minLength: null,
        maxLength: null,
        min: null,
        max: null,
        pattern: '',
        minDate: '',
        maxDate: '',
      },
    });
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const formValue = this.formGroup.getRawValue();

      // Serialize validation rules to JSON string
      const validationRules: ValidationRules = formValue.ValidationRules;
      // Remove null/undefined/empty values and build cleaned object with proper typing
      const cleanedValidationRules: Partial<ValidationRules> = {};

      if (
        validationRules.required !== null &&
        validationRules.required !== undefined
      ) {
        cleanedValidationRules.required = validationRules.required;
      }
      if (
        validationRules.minLength !== null &&
        validationRules.minLength !== undefined
      ) {
        cleanedValidationRules.minLength = validationRules.minLength;
      }
      if (
        validationRules.maxLength !== null &&
        validationRules.maxLength !== undefined
      ) {
        cleanedValidationRules.maxLength = validationRules.maxLength;
      }
      if (validationRules.min !== null && validationRules.min !== undefined) {
        cleanedValidationRules.min = validationRules.min;
      }
      if (validationRules.max !== null && validationRules.max !== undefined) {
        cleanedValidationRules.max = validationRules.max;
      }
      if (validationRules.pattern && validationRules.pattern.trim() !== '') {
        cleanedValidationRules.pattern = validationRules.pattern;
      }
      if (validationRules.minDate && validationRules.minDate.trim() !== '') {
        cleanedValidationRules.minDate = validationRules.minDate;
      }
      if (validationRules.maxDate && validationRules.maxDate.trim() !== '') {
        cleanedValidationRules.maxDate = validationRules.maxDate;
      }

      // Only include validation rules if there are any rules defined
      const validationRulesJson =
        Object.keys(cleanedValidationRules).length > 0
          ? JSON.stringify(cleanedValidationRules)
          : null;

      const optionsList = (formValue.Options as string[]).filter(
        (opt) => opt && opt.trim(),
      );
      const optionsString =
        optionsList.length > 0 ? optionsList.join(',') : null;

      const newField: any = {
        FieldName: formValue.FieldName,
        DisplayLabel: formValue.DisplayLabel,
        VertexType: formValue.VertexType,
        FieldType: formValue.FieldType,
        ValidationRules: validationRulesJson,
        DisplayOrder: formValue.DisplayOrder,
        IsReadOnly: formValue.IsReadOnly,
        IsHidden: formValue.IsHidden,
        Options: optionsString,
      };

      if (this.customFieldId !== '0') {
        this.customFieldService
          .updateCustomField(this.customFieldId, newField)
          .subscribe({
            next: (updatedField: any) => {
              this.matSnackbar.open(
                'Custom field updated',
                'OK',
                DEFAULT_SNACKBAR_CONFIG,
              );
              this.customFieldService.reload();
              this.router.navigate(['../'], { relativeTo: this.route });
            },
            error: (err: any) => {
              console.error('Error updating custom field:', err);
            },
          });
        return;
      } else {
        this.customFieldService.createCustomField(newField).subscribe({
          next: (createdField: any) => {
            this.matSnackbar.open(
              'Custom field created',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
            this.customFieldService.reload();
            this.router.navigate(['../'], { relativeTo: this.route });
          },
          error: (err: any) => {
            this.matSnackbar.open(
              'Error creating custom field',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
          },
        });
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  onDelete(): void {
    if (this.customFieldId !== '0') {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Custom Field?`,
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((result) => {
        if (result === true) {
          this.customFieldService
            .deleteCustomFields([this.customFieldId])
            .subscribe({
              next: () => {
                this.matSnackbar.open(
                  'Custom field deleted',
                  'OK',
                  DEFAULT_SNACKBAR_CONFIG,
                );
                this.customFieldService.reload();
                this.router.navigate(['../'], { relativeTo: this.route });
              },
              error: (err: any) => {
                this.matSnackbar.open(
                  'Error deleting custom field',
                  'OK',
                  DEFAULT_SNACKBAR_CONFIG,
                );
              },
            });
        }
      });
    }
  }
}